import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { FC, useCallback, useMemo, useState } from "react";

import { RadioButtons } from "~/src/components/common/RadioButtons";
import { Select, type SelectProps } from "~/src/components/common/Select";
import { Tooltip } from "~/src/components/common/Tooltip";
import { URLItem } from "~/src/components/common/URLItem";
import { PageLayout } from "~/src/components/features/global/PageLayout";

import {
  ContentElementMetric,
  ContentMetricsTable,
} from "./presentations/ContentMetricsTable";
import {
  ContentPreviewImage,
  OverlayMetricMode,
} from "./presentations/ContentPreviewImage";

const DEVICE_OPTIONS = [
  { label: "PC", value: "PC" },
  { label: "モバイル", value: "MOBILE" },
] as const;

const OVERLAY_MODE_OPTIONS = [
  { label: "クリック率", value: "clicks" },
  { label: "ゴール率", value: "goals" },
  { label: "両方", value: "both" },
] as const;

type Breadcrumb = {
  label: string;
  path: string;
};

export type Props = {
  title: string;
  breadcrumbs: Breadcrumb[];
  pageUrl: string;
  deviceType: string;
  onDeviceChange: (deviceType: string) => void;
  totalUniqueUsers: number;
  totalGoals: number;
  totalViews: number;
  viewportWidth: number | null | undefined;
  viewportHeight: number | null | undefined;
  previewImageUrl: string | null | undefined;
  elements: ContentElementMetric[];
};

const MetricItem: FC<{
  label: string;
  value: string | number;
  tooltip: string;
}> = ({ label, value, tooltip }) => (
  <Flex
    flex={1}
    alignItems="center"
    justifyContent="space-between"
    p={4}
    bg="#FAFAFA"
    border="1px solid #DADADA"
    borderRadius="4px"
  >
    <Flex alignItems="center" gap={1}>
      <Text fontSize="13px" fontWeight="bold" color="#666666">
        {label}
      </Text>
      <Tooltip
        label={tooltip}
        placement="top"
        sx={{ whiteSpace: "pre-line", maxWidth: "280px" }}
      >
        <Box as="span" display="inline-flex">
          <HelpOutlineOutlinedIcon sx={{ fontSize: 14 }} />
        </Box>
      </Tooltip>
    </Flex>
    <Text fontSize="18px" fontWeight="bold" color="#282828">
      {value}
    </Text>
  </Flex>
);

const formatGoalRate = (
  totalGoals: number,
  totalUniqueUsers: number
): string => {
  if (totalUniqueUsers === 0) return "0%";
  return `${parseFloat(((totalGoals / totalUniqueUsers) * 100).toFixed(2))}%`;
};

export const ContentReportPage: FC<Props> = ({
  title,
  breadcrumbs,
  pageUrl,
  deviceType,
  onDeviceChange,
  totalUniqueUsers,
  totalGoals,
  totalViews: _totalViews,
  viewportWidth,
  viewportHeight,
  previewImageUrl,
  elements,
}) => {
  const [overlayMode, setOverlayMode] = useState<OverlayMetricMode>("clicks");

  const selectedDeviceOption =
    DEVICE_OPTIONS.find((opt) => opt.value === deviceType) ?? DEVICE_OPTIONS[0];

  const previewElements = useMemo(
    () =>
      elements.map((el) => ({
        name: el.name,
        clickRate: el.clickRate,
        goalRate: el.goalRate,
        position: el.position,
      })),
    [elements]
  );

  const handleDeviceChange = useCallback<NonNullable<SelectProps["onChange"]>>(
    (option) => {
      if (option && "value" in option) {
        onDeviceChange(option.value as string);
      }
    },
    [onDeviceChange]
  );

  const handleOverlayModeChange = useCallback((value: string) => {
    setOverlayMode(value as OverlayMetricMode);
  }, []);

  const previewImage = (
    <ContentPreviewImage
      previewImageUrl={previewImageUrl}
      viewportWidth={viewportWidth}
      viewportHeight={viewportHeight}
      elements={previewElements}
      overlayMode={overlayMode}
      imageWidth={deviceType === "MOBILE" ? "80%" : "100%"}
    />
  );

  return (
    <PageLayout title={title} breadcrumbs={breadcrumbs}>
      <Box mx={4} mt={4}>
        <Flex alignItems="center" gap={4} mb={4}>
          <Box width="160px">
            <Select
              options={DEVICE_OPTIONS}
              value={selectedDeviceOption}
              onChange={handleDeviceChange}
            />
          </Box>
          <URLItem url={pageUrl} />
        </Flex>

        <Flex gap={4} mb={6} alignItems="stretch">
          <MetricItem
            label="ユーザー数"
            value={totalUniqueUsers.toLocaleString()}
            tooltip={
              "集計期間中にサイトを訪問したユニークユーザー数。\n同一ユーザーが集計期間中に複数回訪問した場合、1ユーザーとしてカウントされます。"
            }
          />
          <MetricItem
            label="ゴール数"
            value={totalGoals.toLocaleString()}
            tooltip={
              "以下の条件を満たした場合にビューとしてカウントされます：\n・コンテンツの50%以上がビューポートに表示されていること\n・かつ、累計表示時間が2秒以上であること"
            }
          />
          <MetricItem
            label="ゴール率"
            value={formatGoalRate(totalGoals, totalUniqueUsers)}
            tooltip={
              "ゴール率 = ゴール数 ÷ ユニークユーザー数\nゴール = ユーザーがファネルのサンクスページに到達すること\nゴール数 = ゴールを達成したユーザーによるコンテンツビュー数"
            }
          />
        </Flex>

        <Flex alignItems="center" gap={3} mb={3}>
          <Text fontSize="13px" fontWeight="bold" color="#666666">
            オーバーレイ表示
          </Text>
          <RadioButtons
            name="overlayMetricMode"
            value={overlayMode}
            options={OVERLAY_MODE_OPTIONS}
            onChange={handleOverlayModeChange}
          />
          <Tooltip
            label={
              "クリック率：要素ごとのクリック率を表示\nゴール率：要素ごとのゴール率を表示\n両方：クリック率（上）とゴール率（下）を同時表示"
            }
            placement="top"
            sx={{ whiteSpace: "pre-line", maxWidth: "280px" }}
          >
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>

        {deviceType === "PC" ? (
          <VStack spacing={4} alignItems="stretch">
            {previewImage}
            <ContentMetricsTable elements={elements} />
          </VStack>
        ) : (
          <Flex gap={4} alignItems="flex-start">
            <Box width="25%" flexShrink={0}>
              {previewImage}
            </Box>
            <Box flex={1} overflow="hidden">
              <ContentMetricsTable elements={elements} />
            </Box>
          </Flex>
        )}
      </Box>
    </PageLayout>
  );
};
